/**
 * What the student has been DOING, as the person standing next to them saw it.
 *
 * NOTHING NEW IS LOGGED HERE, and that is the point. Every event this reads
 * was already being recorded: `panelStore` writes a `visit` the moment a
 * panel opens, the panels write `recipeChange` when a knob moves, `wafer`
 * and `product` when something runs, `reading` when a measurement is taken,
 * `maintenance` and `call` for the rest. That trail is the record an
 * instructor reads and the thing the work-order evaluator matches on
 * (learn/Journal.ts). Building a second log beside it would have given the
 * floor a version of the afternoon that could disagree with the one the
 * student hands in - so the technicians read the same journal.
 *
 * WHO GETS WHAT, and it is the same line the rest of the brief draws.
 *
 *   `workAt`    is for the technician at that tool, and it is detailed:
 *               the knob you moved and what you moved it to, the wafer you
 *               ran and what it came out at, whether it was in spec. This
 *               is the STRONGEST case in the whole system for a technician
 *               knowing something - they are standing at the HMI, it is
 *               their machine, and they watched you do it. A technician who
 *               cannot see the wafer you just ran in front of them is the
 *               one thing on this floor nobody would believe.
 *
 *   `workRound` is for the shift supervisor, and it is COUNTS: which tools
 *               were opened, how many runs, how many out of spec, how many
 *               calls and whether they were right. No measured values. He
 *               reads boards and move counts down the aisle; a rate in
 *               nm/min is not on a board and is not his to quote, exactly
 *               as with `status` in brief.ts.
 *
 * HOW AN EVENT IS ATTRIBUTED TO A TOOL. The journal's `tool` field is not
 * one thing: `visit` carries the stable layout id ('etch'), `product`
 * carries the asset tag ('CZ-07'), `maintenance` carries a condition
 * model's `toolLabel` ('ETCH-01 - ICP polysilicon etch'), and `wafer`,
 * `recipeChange` and `question` carry nothing at all. Rather than rewrite
 * twenty panels to agree - a wide, risky edit for a read-only feature -
 * `owns` accepts any of the three spellings, and the untooled events go to
 * whichever shift was open, which is the same rule `stationSummary` and
 * `currentShiftEvents` already use. They are recorded by the panel the
 * student is standing at, so the open shift is where they belong.
 */

import type { Journal, JournalEvent } from '../learn/Journal'
import { UNATTRIBUTED } from '../learn/shift'
import type { Tool } from '../fab/layout'

/** One thing the student did, in the terms the person beside them would use. */
export interface WorkEvent {
  /** What kind of thing, so the prompt can phrase it. */
  kind: 'opened' | 'knob' | 'ran' | 'read' | 'maintenance' | 'call' | 'called-it'
  /** The line itself, already in the student's language where it has words. */
  text: string
  /** Minutes ago, the way anyone standing there would say it. */
  ago: number
}

/** What one tool saw, for a supervisor who counts rather than measures. */
export interface WorkTally {
  tag: string
  bay: string
  runs: number
  outOfSpec: number
  readings: number
  calls: number
  /** Calls that were right. */
  correct: number
  /** Minutes since the last thing they did there. */
  ago: number
}

/** The last few, which is as much as anyone standing there would recall. */
const WORK_MAX = 6
const TALLY_MAX = 6

/**
 * How far back anybody on this floor remembers: a sitting, not a career.
 *
 * The journal is restored from the last session (`attachSession`), because
 * the record an instructor reads and the work order's evidence both have to
 * survive a reload. The PEOPLE do not: the supervisor's tally came back
 * listing LITH-03, THRM-04 and CMP-11 with nothing done in any of them and
 * "last 1563 min ago", which is yesterday afternoon, which is not a thing
 * he watched. Three hours covers a sitting with a reload in the middle of
 * it and excludes the day before.
 *
 * Nothing downstream of the journal is bounded by this. The tool's own
 * debrief still uses `currentShiftEvents`, and the export still has the
 * whole afternoon in it - this is only what someone standing next to you
 * would say they had seen.
 */
const REMEMBERED_MS = 3 * 3_600_000

/**
 * Does this event belong to this tool, however the recorder spelled it?
 * `undefined` means it carries no tool and belongs to the open shift.
 */
function owns(e: JournalEvent, tool: Tool): boolean | undefined {
  const named = 'tool' in e ? e.tool : undefined
  const id = 'toolId' in e ? e.toolId : undefined
  if (id !== undefined) return id === tool.id
  if (named === undefined || named === UNATTRIBUTED) return undefined
  // A condition model's label leads with the tag: 'ETCH-01 - ICP polysilicon etch'.
  return named === tool.id || named === tool.assetTag || named.startsWith(`${tool.assetTag} `)
}

/** Which tool an event names at all, for the supervisor's per-tool tally. */
function namedTool(e: JournalEvent, tools: Tool[]): Tool | undefined {
  const named = 'tool' in e ? e.tool : undefined
  const id = 'toolId' in e ? e.toolId : undefined
  return tools.find((t) => (id !== undefined ? id === t.id : named !== undefined && owns(e, t) === true))
}

const minutesAgo = (at: number, now: number): number => Math.max(0, Math.round((now - at) / 60_000))

/**
 * What the student has done at THIS tool, oldest first.
 *
 * `open` tracks the shift the untooled events belong to as the walk goes,
 * the same way `stationSummary` does - a wafer run carries no tool, and the
 * one it belongs to is whichever shift was open when it ran.
 */
export function workAt(journal: Journal, tool: Tool): WorkEvent[] {
  const now = Date.now()
  const out: WorkEvent[] = []
  let open = UNATTRIBUTED
  const since = now - REMEMBERED_MS
  for (const e of journal.events) {
    if (e.kind === 'shiftStart') open = e.toolId ?? e.tool ?? UNATTRIBUTED
    if (e.at < since) continue
    const mine = owns(e, tool)
    // An untooled event is this tool's only while this tool's shift is open.
    if (mine === false) continue
    if (mine === undefined && open !== tool.id && open !== tool.assetTag && !open.startsWith(`${tool.assetTag} `)) continue
    const ago = minutesAgo(e.at, now)
    switch (e.kind) {
      case 'visit':
        out.push({ kind: 'opened', text: 'opened the panel', ago })
        break
      case 'recipeChange':
        out.push({ kind: 'knob', text: `set ${e.knob} from ${e.from} to ${e.to}`, ago })
        break
      case 'wafer':
        out.push({
          kind: 'ran',
          text: `ran a wafer: ${e.rateNmMin.toFixed(0)} nm/min, ${e.nonUniformityPct.toFixed(1)}% non-uniformity, ${e.inSpec ? 'in spec' : 'OUT OF SPEC'}`,
          ago,
        })
        break
      case 'product':
        out.push({
          kind: 'ran',
          text: `ran ${e.count} ${e.unit}${e.count === 1 ? '' : 's'}: ${e.metric} ${e.value} ${e.valueUnit}, ${e.inSpec ? 'in spec' : 'OUT OF SPEC'}`,
          ago,
        })
        break
      case 'reading':
        out.push({ kind: 'read', text: `took a reading: ${e.what}`, ago })
        break
      case 'maintenance':
        out.push({ kind: 'maintenance', text: `did maintenance: ${e.label ?? e.action}`, ago })
        break
      case 'call':
        out.push({ kind: 'call', text: `called it as ${e.label} - ${e.correct ? 'right' : 'wrong'}`, ago })
        break
      // The one event that carries what they THOUGHT. Both halves in one
      // line, in the order they happened, and no verdict on it - saying
      // whether the call matched the measurement is the technician's, out
      // loud, from two things she can both see. See predict.ts.
      //
      // Phrased with the STUDENT as the subject, like every other line here:
      // the prompt renders each one as "they {text}", and a line written from
      // the technician's side came out as "they you asked them to call...".
      // What they made of it when she asked. Its own line rather than folded
      // into the call above, because it arrives a turn later and she may well
      // be answering it right now.
      case 'explanation':
        out.push({ kind: 'called-it', text: `when you asked why "${e.about}" went that way, they said: "${e.said}"`, ago })
        break
      case 'prediction':
        out.push({
          kind: 'called-it',
          text: `called "${e.about}" before the run and said: "${e.said}" - the tool then measured: ${e.measured}`,
          ago,
        })
        break
      default:
        // handoff, place, question, orderComplete, shiftStart: either
        // somebody else's business, or already in the brief elsewhere.
        // Questions in particular are the talk log's job (talkLog.ts).
        break
    }
  }
  return out.slice(-WORK_MAX)
}

/**
 * Which tools the student has actually been INTO, and how it went there, as
 * counts. The supervisor's version: he sees people come out of a bay, and
 * the boards tell him how many moves and how many were scrapped.
 */
export function workRound(journal: Journal, tools: Tool[], bayOf: (tool: Tool) => string): WorkTally[] {
  const rows = new Map<string, WorkTally & { last: number }>()
  const now = Date.now()
  let open: Tool | undefined
  const row = (tool: Tool): WorkTally & { last: number } => {
    let found = rows.get(tool.id)
    if (!found) {
      found = { tag: tool.assetTag, bay: bayOf(tool), runs: 0, outOfSpec: 0, readings: 0, calls: 0, correct: 0, ago: 0, last: 0 }
      rows.set(tool.id, found)
    }
    return found
  }
  const since = now - REMEMBERED_MS
  for (const e of journal.events) {
    if (e.kind === 'shiftStart') open = namedTool(e, tools) ?? open
    if (e.at < since) continue
    const at = namedTool(e, tools) ?? (('tool' in e ? e.tool : undefined) === undefined ? open : undefined)
    if (!at) continue
    const r = row(at)
    r.last = Math.max(r.last, e.at)
    switch (e.kind) {
      case 'wafer':
      case 'product':
        r.runs += e.kind === 'product' ? e.count : 1
        if (!e.inSpec) r.outOfSpec += e.kind === 'product' ? e.count : 1
        break
      case 'reading':
        r.readings++
        break
      case 'call':
        r.calls++
        if (e.correct) r.correct++
        break
      default:
        break
    }
  }
  return [...rows.values()]
    .sort((a, b) => a.last - b.last)
    .slice(-TALLY_MAX)
    .map(({ last, ...r }) => ({ ...r, ago: minutesAgo(last, now) }))
}
